// bluaIQ — P2: AIA G702/G703 Billing Module
// AIA-style draw request document generator with auto-calculations

import { apiFetch } from '../utils/api'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FileText, Download, Plus, Calculator, DollarSign, Info } from 'lucide-react'
import { isDemoProject } from '../utils/demoData'
import { downloadCsv } from '../utils/exportCsv'
import { calculateDrawLine, calculateDrawSummary } from '../services/drawRequestSummary'
// Financial Trust P0 (PR-F3): safe money/percent so AIA never renders NaN/Infinity
// as money or an absurd raw percentage; mixed contract/budget sources are disclosed.
import { formatMoney, formatMoneyValue, formatPercent } from '../utils/money'
// PR-F3A.1: anomalous-amount guard so a persisted/edited draw request can never
// surface an anomalous contract amount (>= $1M, non-finite, <= 0) as the VISIBLE
// AIA contract basis or in billing math.
import { isAnomalousContractAmount } from '../services/financialTrustGuards'

// ── PR-F3A.1: AIA contract-basis source-review copy ───────────────────────────
// Shown when the VISIBLE contract basis (persisted draw request contractSum) is
// anomalous. The anomalous amount is excluded from the billing basis ($0 basis)
// and surfaced only as source-review context, never as a usable billing value.
const PRF3A1_COPY = {
  excludedFromBasis: {
    en: 'Excluded from AIA contract basis until the contract source is reviewed.',
    es: 'Excluido de la base del contrato para AIA hasta revisar la fuente del contrato.',
  },
  cannotUse: {
    en: 'AIA billing cannot use this contract amount until it is verified.',
    es: 'AIA Billing no puede usar este monto de contrato hasta que sea verificado.',
  },
  originalExcludes: {
    en: 'Original contract sum excludes amounts pending source review.',
    es: 'La suma original del contrato excluye montos pendientes de revisión de fuente.',
  },
} as const

// PR-F3B: seed-line exclusion copy — shown when bootstrap fallback items excluded
// anomalous contracts. Applies to the no-budget fallback path only.
const PRF3B_COPY = {
  lineItemsExclude: {
    en: 'AIA line items exclude contract amounts pending source review.',
    es: 'Las partidas de AIA excluyen montos de contrato pendientes de revisión de fuente.',
  },
  noTrustedItems: {
    en: 'No trusted contract line items are available yet. Review contract sources before using them for AIA billing.',
    es: 'Todavía no hay partidas de contrato confiables disponibles. Revise las fuentes de contrato antes de usarlas en AIA Billing.',
  },
  excludedFromLineItems: {
    en: 'Excluded from AIA line items until source review is complete.',
    es: 'Excluido de las partidas de AIA hasta completar la revisión de fuente.',
  },
  persistedItemsExcluded: {
    en: 'Some existing AIA line items are excluded pending source review.',
    es: 'Algunas partidas existentes de AIA están excluidas mientras se revisa la fuente.',
  },
} as const

// ── TYPES ─────────────────────────────────────────

interface DrawItem {
  id: string
  description: string
  descriptionEs?: string
  scheduledValue: number
  previousCompleted: number
  thisperiodCompleted: number
  storedMaterials: number
}

interface DrawRequest {
  id: string
  number: number
  periodTo: string
  contractSum: number
  changeOrderTotal: number
  retainagePercent: number
  status?: string
  items: DrawItem[]
}

interface DrawRequestBootstrap {
  number: number
  periodTo: string
  contractSum: number
  changeOrderTotal: number
  retainagePercent: number
  canCreate: boolean
  items: DrawItem[]
  // PR-F3A: anomalous contract exclusion metadata.
  anomalousContractCount?: number
  anomalousContractAmount?: number
  // PR-F3B: seed-line exclusion metadata.
  seedLineExcludedCount?: number
  seedLineExcludedAmount?: number
  hasSeedLineExclusions?: boolean
}

// ── DEMO DATA ─────────────────────────────────────

const DEMO_DRAW: DrawRequest = {
  id: 'draw-1',
  number: 3,
  periodTo: '2026-03-31',
  contractSum: 285000,
  changeOrderTotal: 6000,
  retainagePercent: 10,
  items: [
    { id: 'di-1', description: 'General Conditions', descriptionEs: 'Condiciones Generales', scheduledValue: 14250, previousCompleted: 7125, thisperiodCompleted: 3562, storedMaterials: 0 },
    { id: 'di-2', description: 'Site Work', descriptionEs: 'Preparación del Terreno', scheduledValue: 12000, previousCompleted: 12000, thisperiodCompleted: 0, storedMaterials: 0 },
    { id: 'di-3', description: 'Foundation', descriptionEs: 'Cimentación', scheduledValue: 18500, previousCompleted: 18500, thisperiodCompleted: 0, storedMaterials: 0 },
    { id: 'di-4', description: 'Framing', descriptionEs: 'Estructura', scheduledValue: 42000, previousCompleted: 21000, thisperiodCompleted: 14700, storedMaterials: 0 },
    { id: 'di-5', description: 'Roofing', descriptionEs: 'Techo', scheduledValue: 12800, previousCompleted: 0, thisperiodCompleted: 0, storedMaterials: 4557 },
    { id: 'di-6', description: 'Plumbing', descriptionEs: 'Plomería', scheduledValue: 15000, previousCompleted: 0, thisperiodCompleted: 3000, storedMaterials: 0 },
    { id: 'di-7', description: 'Electrical', descriptionEs: 'Eléctrico', scheduledValue: 13000, previousCompleted: 0, thisperiodCompleted: 2600, storedMaterials: 0 },
    { id: 'di-8', description: 'HVAC', descriptionEs: 'HVAC', scheduledValue: 10000, previousCompleted: 0, thisperiodCompleted: 0, storedMaterials: 0 },
    { id: 'di-9', description: 'Insulation & Drywall', descriptionEs: 'Aislamiento y Tablaroca', scheduledValue: 21700, previousCompleted: 0, thisperiodCompleted: 0, storedMaterials: 0 },
    { id: 'di-10', description: 'Finishes', descriptionEs: 'Acabados', scheduledValue: 45000, previousCompleted: 0, thisperiodCompleted: 0, storedMaterials: 0 },
    { id: 'di-11', description: 'Exterior & Landscaping', descriptionEs: 'Exterior y Paisajismo', scheduledValue: 22000, previousCompleted: 0, thisperiodCompleted: 0, storedMaterials: 0 },
    { id: 'di-12', description: 'CO: Quartz Countertops', descriptionEs: 'OC: Cubiertas de Cuarzo', scheduledValue: 4200, previousCompleted: 0, thisperiodCompleted: 0, storedMaterials: 0 },
    { id: 'di-13', description: 'CO: Recessed Lighting', descriptionEs: 'OC: Iluminación Empotrada', scheduledValue: 1800, previousCompleted: 0, thisperiodCompleted: 0, storedMaterials: 0 },
    { id: 'di-14', description: 'Contingency', descriptionEs: 'Contingencia', scheduledValue: 11500, previousCompleted: 0, thisperiodCompleted: 0, storedMaterials: 0 },
  ],
}

// ── COMPONENT ─────────────────────────────────────

export default function AIABillingModule() {
  const { getToken } = useAuth()
  const { projectId } = useParams()
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const isEs = i18n.language === 'es'
  const [notice, setNotice] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [draftStatus, setDraftStatus] = useState('DRAFT')
  const [draftPeriodTo, setDraftPeriodTo] = useState('')
  const [draftRetainage, setDraftRetainage] = useState(10)
  const [draftItems, setDraftItems] = useState<DrawItem[]>([])

  const showNotice = (message: string) => {
    setNotice(message)
    setTimeout(() => setNotice(null), 3000)
  }

  const { data: apiDraw, isLoading } = useQuery<DrawRequest[] | DrawRequest>({
    queryKey: ['draw-requests', projectId],
    queryFn: async () => { const token = await getToken(); return apiFetch(`/api/projects/${projectId}/draw-requests`, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => {
      if (!r.ok) throw new Error('API unavailable')
      return r.json()
    }) },
    retry: false,
  })

  const { data: bootstrap } = useQuery<DrawRequestBootstrap>({
    queryKey: ['draw-request-bootstrap', projectId],
    queryFn: async () => {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/draw-requests/bootstrap`, {
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      })
      if (!res.ok) throw new Error('DRAW_BOOTSTRAP_FAILED')
      return res.json()
    },
    retry: false,
  })

  // PR-F3B: Defensive filter — removes any bootstrap items whose scheduledValue is
  // anomalous (>= $1M, non-finite, <= 0). The backend already filters seed lines, but
  // an older cached payload or a raw pre-F3B response must not surface $456M-scale
  // amounts as billable AIA line items. Used for both projected-items count and the
  // create-draw mutation body.
  const safeBootstrapItems = (bootstrap?.items ?? []).filter(
    item => !isAnomalousContractAmount(item.scheduledValue),
  )

  const createDrawMutation = useMutation({
    mutationFn: async () => {
      if (!bootstrap?.canCreate) throw new Error('DRAW_BOOTSTRAP_UNAVAILABLE')
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/draw-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          number: bootstrap.number,
          periodTo: bootstrap.periodTo,
          contractSum: bootstrap.contractSum,
          changeOrderTotal: bootstrap.changeOrderTotal,
          retainagePercent: bootstrap.retainagePercent,
          items: safeBootstrapItems,
        }),
      })
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'DRAW_CREATE_FAILED')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['draw-requests', projectId] })
      queryClient.invalidateQueries({ queryKey: ['draw-request-bootstrap', projectId] })
      showNotice(t('aiaBilling.toast.created'))
    },
    onError: (error: Error) => showNotice(error.message),
  })

  const updateDrawMutation = useMutation({
    mutationFn: async () => {
      if (!draw?.id) throw new Error('DRAW_NOT_FOUND')
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/draw-requests/${draw.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        // A draw's lines and figures are editable only while it is a DRAFT
        // (server R-7.a). Once submitted, the only permitted change is a status
        // transition, so a non-DRAFT save sends ONLY the status — sending frozen
        // content would be rejected 409 draw_not_editable.
        body: JSON.stringify(
          (draw?.status ?? 'DRAFT') === 'DRAFT'
            ? { status: draftStatus, periodTo: draftPeriodTo, retainagePercent: draftRetainage, items: draftItems }
            : { status: draftStatus },
        ),
      })
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'DRAW_UPDATE_FAILED')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['draw-requests', projectId] })
      setIsEditing(false)
      showNotice(isEs ? 'Draw actualizada' : 'Draw updated')
    },
    onError: (error: Error) => showNotice(error.message),
  })

  // API returns an array — pick the latest draw request, or fall back to demo
  const resolvedDraw: DrawRequest | null = (() => {
    if (Array.isArray(apiDraw)) {
      return apiDraw.length > 0 ? apiDraw[0] : null
    }
    if (apiDraw && typeof apiDraw === 'object' && 'id' in apiDraw) {
      return apiDraw as DrawRequest
    }
    return null
  })()

  function formatDate(d: string) {
    if (!d) return '—'
    const date = new Date(d)
    if (isNaN(date.getTime())) return '—'
    return date.toLocaleDateString(isEs ? 'es-MX' : 'en-US', { month: 'long', day: 'numeric', year: 'numeric' })
  }

  const draw = resolvedDraw || (isDemoProject(projectId) ? DEMO_DRAW : null)

  const activeDrawItems = isEditing ? draftItems : (draw?.items || [])
  const activePeriodTo = isEditing ? draftPeriodTo : (draw?.periodTo || '')
  const activeRetainage = isEditing ? draftRetainage : (draw?.retainagePercent ?? 10)
  const activeStatus = isEditing ? draftStatus : (draw?.status || 'DRAFT')

  if (isLoading) return <div className="p-5 animate-pulse"><div className="h-64 bg-[#ECEAE4] rounded-xl" /></div>
  if (!draw) return (
    <div className="p-5 max-w-4xl mx-auto">
      {notice && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-green-600 text-white text-[13px] font-[800] px-4 py-2 rounded-full shadow-lg">
          {notice}
        </div>
      )}
      <div className="text-center py-16">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#06539B] to-[#00BCD4] flex items-center justify-center mx-auto mb-4">
          <span className="text-white text-[24px] font-[900]">G7</span>
        </div>
        <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17]">
          {t('aiaBilling.title')}
        </h2>
        <p className="text-[11px] font-[700] text-[#9E9890] mt-1 max-w-sm mx-auto">
          {t('aiaBilling.subtitle')}
        </p>
        <p className="text-[12px] font-[600] text-[#9E9890] mt-2 max-w-sm mx-auto">
          {t('aiaBilling.empty.description')}
        </p>
        {bootstrap && (
          <div className="mt-5 mx-auto max-w-xl rounded-xl border border-[#ECEAE4] bg-white p-4 text-left">
            <div className="text-[10px] font-[800] uppercase tracking-[0.4px] text-[#06539B]">
              {t('aiaBilling.bootstrap.title')}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-3 text-[11px] font-[700] text-[#1C1A17]">
              <div>
                <div className="text-[#9E9890]">{t('aiaBilling.bootstrap.drawNumber')}</div>
                <div>#{bootstrap.number}</div>
              </div>
              <div>
                <div className="text-[#9E9890]">{t('aiaBilling.bootstrap.suggestedPeriod')}</div>
                <div>{formatDate(bootstrap.periodTo)}</div>
              </div>
              <div>
                <div className="text-[#9E9890]">{t('aiaBilling.bootstrap.contractBasis')}</div>
                {/* PR-F3A.1: defensive — backend already returns the trusted total,
                    but guard the visible basis here too so an older/raw bootstrap
                    payload can never display an anomalous amount as the basis. */}
                <div>{formatMoneyValue(isAnomalousContractAmount(bootstrap.contractSum) ? 0 : bootstrap.contractSum, isEs)}</div>
              </div>
              <div>
                <div className="text-[#9E9890]">{t('aiaBilling.bootstrap.projectedItems')}</div>
                <div>{safeBootstrapItems.length}</div>
              </div>
            </div>
          </div>
        )}
        {/* PR-F3B: seed-line exclusion banner — shown when bootstrap fallback items
            excluded one or more anomalous contracts (no-budget-lines path only).
            Prevents user from creating a draw with anomalous scheduled values. */}
        {bootstrap?.hasSeedLineExclusions && (
          <div
            data-testid="aia-seedline-exclusion-banner"
            className="mt-3 mx-auto max-w-xl flex flex-col gap-1 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[10px] font-[700] text-red-700 text-left"
          >
            <span data-testid="aia-seedline-exclude-line">{isEs ? PRF3B_COPY.lineItemsExclude.es : PRF3B_COPY.lineItemsExclude.en}</span>
            <span data-testid="aia-seedline-excluded-from-line">{isEs ? PRF3B_COPY.excludedFromLineItems.es : PRF3B_COPY.excludedFromLineItems.en}</span>
            {safeBootstrapItems.length === 0 && (
              <span data-testid="aia-seedline-no-trusted-line">{isEs ? PRF3B_COPY.noTrustedItems.es : PRF3B_COPY.noTrustedItems.en}</span>
            )}
          </div>
        )}
        <button
          onClick={() => createDrawMutation.mutate()}
          disabled={!bootstrap?.canCreate || createDrawMutation.isPending}
          className="mt-5 inline-flex items-center gap-1.5 bg-[#06539B] text-white text-[13px] font-[800] px-5 py-2.5 rounded-full hover:bg-[#14457E] transition-colors disabled:opacity-40"
        >
          + {createDrawMutation.isPending ? t('aiaBilling.creating') : t('aiaBilling.empty.newDrawRequest')}
        </button>
      </div>
    </div>
  )

  // ── Safe accessors ──────────────────────────────
  // PR-F3B: Read-side guard for persisted draw items. An existing draw request may
  // carry DrawItems with anomalous scheduledValue (>= $1M, non-finite, <= 0) captured
  // before the F3B bootstrap guard existed. Exclude from billable display/calculations/
  // CSV export. Draw data is not mutated — display-only derivation.
  const safeDrawItems = activeDrawItems.filter(item => !isAnomalousContractAmount(item.scheduledValue))
  const persistedItemExcludedCount = activeDrawItems.length - safeDrawItems.length
  // PR-F3A.1: the persisted draw request may carry a contractSum captured BEFORE
  // the PR-F3A backend bootstrap guard existed (or directly edited). Guard the
  // VISIBLE basis here so an anomalous amount (>= $1M, non-finite, <= 0) is never
  // shown as the AIA Original/Adjusted Contract Sum nor used in billing math. The
  // raw value is surfaced only as excluded source-review context (banner), never
  // as a usable billing basis. Frontend display-only — no draw write/mutation.
  const rawContractSum = draw.contractSum ?? 0
  const contractSumIsAnomalous = isAnomalousContractAmount(rawContractSum)
  const contractSum = contractSumIsAnomalous ? 0 : rawContractSum
  const changeOrderTotal = draw.changeOrderTotal ?? 0
  const retainagePercent = activeRetainage
  const drawNumber = draw.number ?? 1
  const periodTo = activePeriodTo

  // ── Calculations ──────────────────────────────
  const adjustedContractSum = contractSum + changeOrderTotal
  const summary = calculateDrawSummary({
    contractSum,
    changeOrderTotal,
    retainagePercent,
    items: safeDrawItems,
  })
  const totals = {
    scheduled: summary.scheduledTotal,
    previousCompleted: summary.previousCompletedTotal,
    thisperiod: summary.thisPeriodTotal,
    stored: summary.storedMaterialsTotal,
  }
  const totalCompletedAndStored = summary.totalCompletedAndStored
  const retainageAmount = summary.retainageAmount
  const amountDue = summary.amountDue
  const pctComplete = summary.percentComplete

  const exportCsv = () => {
    downloadCsv({
      // Customer-visible filename. Avoid implying this is an
      // official AIA G702/G703 deliverable; the file is an
      // AIA-style continuation worksheet for reference only.
      filename: `progress-billing-continuation-draw-${drawNumber}-${projectId}`,
      headers: [
        t('aiaBilling.csv.description'),
        t('aiaBilling.csv.scheduledValue'),
        t('aiaBilling.csv.previousCompleted'),
        t('aiaBilling.csv.thisPeriod'),
        t('aiaBilling.csv.storedMaterials'),
        t('aiaBilling.csv.totalCompleted'),
        '%',
      ],
      rows: safeDrawItems.map(item => {
        const line = calculateDrawLine(item)
        return [isEs ? (item.descriptionEs || item.description) : item.description, item.scheduledValue ?? 0, item.previousCompleted ?? 0, item.thisperiodCompleted ?? 0, item.storedMaterials ?? 0, line.totalCompletedAndStored, line.percentComplete]
      }),
    })
  }

  const exportPdf = async () => {
    if (!projectId || !draw?.id) return
    try {
      const token = await getToken()
      const params = new URLSearchParams({ lang: isEs ? 'es' : 'en' })
      const res = await apiFetch(`/api/projects/${projectId}/draw-requests/${draw.id}/pdf?${params}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
      if (!res.ok) throw new Error('DRAW_PDF_EXPORT_FAILED')
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      // Customer-visible filename. AIA-style payment-application
      // package — not an official AIA G702/G703 deliverable.
      a.download = `payment-application-draw-${drawNumber}-${projectId}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('[aia billing export pdf]', error)
      showNotice(isEs ? 'No se pudo exportar el PDF' : 'Could not export PDF')
    }
  }

  const startEditing = () => {
    setDraftStatus(draw.status || 'DRAFT')
    setDraftPeriodTo(draw.periodTo || new Date().toISOString())
    setDraftRetainage(draw.retainagePercent ?? 10)
    setDraftItems((draw.items || []).map(item => ({ ...item })))
    setIsEditing(true)
  }

  const updateDraftItem = (itemId: string, field: 'previousCompleted' | 'thisperiodCompleted' | 'storedMaterials', value: number) => {
    setDraftItems(current => current.map(item => item.id === itemId ? { ...item, [field]: Math.max(0, value || 0) } : item))
  }

  return (
    <div className="p-5 max-w-5xl mx-auto space-y-4">
      {notice && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-green-600 text-white text-[13px] font-[800] px-4 py-2 rounded-full shadow-lg">
          {notice}
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17]">
            {t('aiaBilling.title')}
          </h2>
          <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">
            {t('aiaBilling.applicationForPayment')} #{drawNumber} · {formatDate(periodTo)}
          </p>
          <p className="text-[10px] font-[600] text-[#9E9890] mt-1 max-w-2xl">
            {t('aiaBilling.disclaimer')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
          <button
            onClick={exportCsv}
            className="flex items-center gap-1.5 border border-[#ECEAE4] text-[#1C1A17] text-[13px] font-[800] px-4 py-2.5 rounded-full hover:bg-[#F2F0EB] transition-colors"
          >
            <Download size={14} /> CSV
          </button>
          <button
            onClick={() => createDrawMutation.mutate()}
            disabled={!bootstrap?.canCreate || createDrawMutation.isPending}
            className="flex items-center gap-1.5 border border-[#ECEAE4] text-[#1C1A17] text-[13px] font-[800] px-4 py-2.5 rounded-full hover:bg-[#F2F0EB] transition-colors disabled:opacity-40"
          >
            <Plus size={14} /> {createDrawMutation.isPending ? t('aiaBilling.creating') : t('aiaBilling.empty.newDrawRequest')}
          </button>
          <button
            onClick={() => isEditing ? updateDrawMutation.mutate() : startEditing()}
            disabled={updateDrawMutation.isPending}
            className="flex items-center gap-1.5 border border-[#ECEAE4] text-[#1C1A17] text-[13px] font-[800] px-4 py-2.5 rounded-full hover:bg-[#F2F0EB] transition-colors disabled:opacity-40"
          >
            <DollarSign size={14} /> {isEditing ? (updateDrawMutation.isPending ? (isEs ? 'Guardando...' : 'Saving...') : (isEs ? 'Guardar draw' : 'Save draw')) : (isEs ? 'Editar draw' : 'Edit draw')}
          </button>
          {isEditing && (
            <button
              onClick={() => setIsEditing(false)}
              className="flex items-center gap-1.5 border border-[#ECEAE4] text-[#9E9890] text-[13px] font-[800] px-4 py-2.5 rounded-full hover:bg-[#F2F0EB] transition-colors"
            >
              {isEs ? 'Cancelar' : 'Cancel'}
            </button>
          )}
          <button
            onClick={exportPdf}
            className="flex items-center gap-1.5 bg-[#06539B] text-white text-[13px] font-[800] px-4 py-2.5 rounded-full"
          >
            <FileText size={14} /> {t('aiaBilling.exportPdf')}
          </button>
        </div>
      </div>

      {isEditing && (
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="text-[11px] font-[700] text-[#4B5563]">
              {isEs ? 'Estado' : 'Status'}
              <select
                value={draftStatus}
                onChange={e => setDraftStatus(e.target.value)}
                className="mt-1 w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600]"
              >
                <option value="DRAFT">DRAFT</option>
                <option value="SUBMITTED">SUBMITTED</option>
                <option value="APPROVED">APPROVED</option>
                <option value="PAID">PAID</option>
              </select>
            </label>
            <label className="text-[11px] font-[700] text-[#4B5563]">
              {isEs ? 'Periodo hasta' : 'Period to'}
              <input
                type="datetime-local"
                value={draftPeriodTo ? new Date(draftPeriodTo).toISOString().slice(0, 16) : ''}
                onChange={e => setDraftPeriodTo(new Date(e.target.value).toISOString())}
                className="mt-1 w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600]"
              />
            </label>
            <label className="text-[11px] font-[700] text-[#4B5563]">
              {isEs ? 'Retención %' : 'Retainage %'}
              <input
                type="number"
                min={0}
                max={100}
                value={draftRetainage}
                onChange={e => setDraftRetainage(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                className="mt-1 w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[12px] font-[600]"
              />
            </label>
          </div>
        </div>
      )}

      {bootstrap && (
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[10px] font-[800] uppercase tracking-[0.4px] text-[#06539B]">
                {t('aiaBilling.bootstrap.title')}
              </div>
              <p className="mt-1 text-[12px] font-[600] text-[#4B5563]">
                {t('aiaBilling.bootstrap.subtitle')}
              </p>
            </div>
            <div className="text-right">
              <div className="text-[11px] font-[700] text-[#9E9890]">{t('aiaBilling.bootstrap.projectedItems')}</div>
              <div className="text-[18px] font-[900] text-[#1C1A17]">{safeBootstrapItems.length}</div>
            </div>
          </div>
        </div>
      )}

      {/* PR-F3: source-alignment honesty — AIA mixes the contract-derived contract
          sum with budget-derived line scheduled values; they may not reconcile.
          Not connected to a payment record / Calendar / Gantt yet. No silent writes. */}
      <div data-testid="aia-source-note" className="flex items-start gap-2 text-[10px] font-[600] text-[#9E6B00] bg-[#FEF7E6] border border-[#F5E2B8] rounded-lg px-3 py-2 leading-relaxed">
        <Info size={12} className="flex-shrink-0 mt-0.5" />
        <span>
          {isEs
            ? 'AIA Billing usa datos de contrato y presupuesto. Revisa la alineación de fuentes antes de enviar. Aún no conectado a un registro de pago ni a Calendar/Gantt.'
            : 'AIA billing uses contract and budget data. Verify source alignment before sending. Not connected to a payment record or Calendar/Gantt yet.'}
        </span>
      </div>

      {/* PR-F3A: anomalous contract exclusion banner — shown when at least one
          contract amount was excluded from the AIA contractSum. The contractSum
          above already reflects only trusted amounts; this note explains why. */}
      {(bootstrap?.anomalousContractCount ?? 0) > 0 && (
        <div
          data-testid="aia-anomalous-contract-banner"
          className="flex items-start gap-2 text-[10px] font-[700] text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 leading-relaxed"
        >
          <Info size={12} className="flex-shrink-0 mt-0.5" />
          <span>
            {isEs
              ? `${bootstrap!.anomalousContractCount} monto${bootstrap!.anomalousContractCount === 1 ? '' : 's'} de contrato excluido${bootstrap!.anomalousContractCount === 1 ? '' : 's'} de la base del AIA — excluido pendiente de revisión de fuente. Revise la fuente del contrato antes de usar este monto en AIA Billing, pagos o proyecciones.`
              : `${bootstrap!.anomalousContractCount} contract amount${bootstrap!.anomalousContractCount === 1 ? '' : 's'} excluded from AIA basis — excluded pending source review. Verify contract source before using this amount for AIA billing, payments, or forecasts.`}
          </span>
        </div>
      )}

      {/* PR-F3A.1: visible-basis source-review banner — fires when the persisted
          draw request's contractSum is itself anomalous (the backend bootstrap
          guard only protects NEW draws; an existing draw can still carry a raw
          pre-guard amount). The Original/Adjusted Contract Sum cards below already
          show $0 trusted basis; this banner explains the exclusion and keeps the
          raw amount out of every billing value. */}
      {contractSumIsAnomalous && (
        <div
          data-testid="aia-contract-basis-excluded-banner"
          className="flex flex-col gap-1 text-[10px] font-[700] text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 leading-relaxed"
        >
          <span data-testid="aia-basis-excluded-line">{isEs ? PRF3A1_COPY.excludedFromBasis.es : PRF3A1_COPY.excludedFromBasis.en}</span>
          <span data-testid="aia-basis-cannotuse-line">{isEs ? PRF3A1_COPY.cannotUse.es : PRF3A1_COPY.cannotUse.en}</span>
          <span data-testid="aia-basis-original-line">{isEs ? PRF3A1_COPY.originalExcludes.es : PRF3A1_COPY.originalExcludes.en}</span>
        </div>
      )}

      {/* PR-F3B: seed-line exclusion informational banner — shown in existing-draw
          view when bootstrap reports anomalous contracts were excluded from fallback
          line items. Informational only: existing draw items are already persisted. */}
      {bootstrap?.hasSeedLineExclusions && (
        <div
          data-testid="aia-seedline-exclusion-banner"
          className="flex flex-col gap-1 text-[10px] font-[700] text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 leading-relaxed"
        >
          <span data-testid="aia-seedline-exclude-line">{isEs ? PRF3B_COPY.lineItemsExclude.es : PRF3B_COPY.lineItemsExclude.en}</span>
          <span data-testid="aia-seedline-excluded-from-line">{isEs ? PRF3B_COPY.excludedFromLineItems.es : PRF3B_COPY.excludedFromLineItems.en}</span>
        </div>
      )}

      {/* PR-F3B: persisted draw item exclusion banner — shown when one or more
          existing draw items carry anomalous scheduledValue from before the F3B
          bootstrap guard. Those items are excluded from G703 table, summary
          calculations, and CSV export. Display-only — no draw data mutated. */}
      {persistedItemExcludedCount > 0 && (
        <div
          data-testid="aia-persisted-item-excluded-banner"
          className="flex flex-col gap-1 text-[10px] font-[700] text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 leading-relaxed"
        >
          <span data-testid="aia-persisted-excluded-line">{isEs ? PRF3B_COPY.persistedItemsExcluded.es : PRF3B_COPY.persistedItemsExcluded.en}</span>
          <span>{isEs ? PRF3B_COPY.lineItemsExclude.es : PRF3B_COPY.lineItemsExclude.en}</span>
        </div>
      )}

      {/* Application for payment summary (AIA-style; for reference only) */}
      <div className="bg-white border border-[#ECEAE4] rounded-xl p-4">
        <h3 className="text-[13px] font-[800] mb-3 flex items-center gap-2">
          <Calculator size={14} /> {t('aiaBilling.g702.summary')}
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <SummaryCard label={t('aiaBilling.g702.originalContractSum')} value={contractSum} />
          <SummaryCard label={t('aiaBilling.g702.changeOrders')} value={changeOrderTotal} accent="#06539B" />
          <SummaryCard label={t('aiaBilling.g702.adjustedContract')} value={adjustedContractSum} accent="#1C1A17" />
          <SummaryCard label={t('aiaBilling.g702.totalCompleted')} value={totalCompletedAndStored} accent="#22C55E" pct={pctComplete} />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
          <SummaryCard label={t('aiaBilling.g702.retainage', { pct: retainagePercent })} value={retainageAmount} accent="#F59E0B" />
          <SummaryCard label={t('aiaBilling.g702.previouslyPaid')} value={totals.previousCompleted} />
          <SummaryCard label={t('aiaBilling.g702.amountDue')} value={amountDue} accent="#22C55E" large />
        </div>
      </div>

      {/* Continuation sheet (AIA-style; for reference only) */}
      <div className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-[#ECEAE4]">
          <h3 className="text-[13px] font-[800]">{t('aiaBilling.g703.continuationSheet')}</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="bg-[#F2F0EB] text-[#9E9890] font-[800] text-left">
                <th className="px-4 py-2">{t('aiaBilling.table.description')}</th>
                <th className="px-3 py-2 text-right">{t('aiaBilling.table.scheduled')}</th>
                <th className="px-3 py-2 text-right">{t('aiaBilling.table.prevCompleted')}</th>
                <th className="px-3 py-2 text-right">{t('aiaBilling.table.thisPeriod')}</th>
                <th className="px-3 py-2 text-right">{t('aiaBilling.table.storedMat')}</th>
                <th className="px-3 py-2 text-right">{t('aiaBilling.table.total')}</th>
                <th className="px-3 py-2 text-right">%</th>
              </tr>
            </thead>
            <tbody>
              {safeDrawItems.map(item => {
                const line = calculateDrawLine(item)
                const sv = line.scheduledValue
                const pc = line.previousCompleted
                const tp = line.thisperiodCompleted
                const sm = line.storedMaterials
                const total = line.totalCompletedAndStored
                const pct = line.percentComplete
                return (
                  <tr key={item.id} className="border-t border-[#F2F0EB] hover:bg-[#FAFAF8]">
                    <td className="px-4 py-2 font-[700] text-[#1C1A17]">{isEs ? (item.descriptionEs || item.description) : item.description}</td>
                    <td className="px-3 py-2 text-right font-[600] text-[#9E9890]">{formatMoneyValue(sv, isEs)}</td>
                    <td className="px-3 py-2 text-right font-[600] text-[#9E9890]">
                      {isEditing ? (
                        <input
                          type="number"
                          min={0}
                          value={pc}
                          onChange={e => updateDraftItem(item.id, 'previousCompleted', Number(e.target.value))}
                          className="w-24 ml-auto border border-[#ECEAE4] rounded px-2 py-1 text-right"
                        />
                      ) : formatMoneyValue(pc, isEs)}
                    </td>
                    <td className="px-3 py-2 text-right font-[700] text-[#1C1A17]">
                      {isEditing ? (
                        <input
                          type="number"
                          min={0}
                          value={tp}
                          onChange={e => updateDraftItem(item.id, 'thisperiodCompleted', Number(e.target.value))}
                          className="w-24 ml-auto border border-[#ECEAE4] rounded px-2 py-1 text-right"
                        />
                      ) : tp > 0 ? formatMoneyValue(tp, isEs) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right font-[600] text-[#9E9890]">
                      {isEditing ? (
                        <input
                          type="number"
                          min={0}
                          value={sm}
                          onChange={e => updateDraftItem(item.id, 'storedMaterials', Number(e.target.value))}
                          className="w-24 ml-auto border border-[#ECEAE4] rounded px-2 py-1 text-right"
                        />
                      ) : sm > 0 ? formatMoneyValue(sm, isEs) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right font-[800] text-[#1C1A17]">{formatMoneyValue(total, isEs)}</td>
                    <td className="px-3 py-2 text-right">
                      <span className={`font-[800] ${pct === 100 ? 'text-[#22C55E]' : pct > 0 ? 'text-[#06539B]' : 'text-[#9E9890]'}`}>
                        {formatPercent(pct, isEs).displayValue}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-[#1C1A17] bg-[#F2F0EB] font-[900] text-[#1C1A17]">
                <td className="px-4 py-2">{t('aiaBilling.g703.totals')}</td>
                <td className="px-3 py-2 text-right">{formatMoneyValue(totals.scheduled, isEs)}</td>
                <td className="px-3 py-2 text-right">{formatMoneyValue(totals.previousCompleted, isEs)}</td>
                <td className="px-3 py-2 text-right">{formatMoneyValue(totals.thisperiod, isEs)}</td>
                <td className="px-3 py-2 text-right">{formatMoneyValue(totals.stored, isEs)}</td>
                <td className="px-3 py-2 text-right">{formatMoneyValue(totalCompletedAndStored, isEs)}</td>
                <td className="px-3 py-2 text-right">{formatPercent(pctComplete, isEs).displayValue}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  )
}

// ── SUMMARY CARD ─────────────────────────────────

function SummaryCard({ label, value, accent, pct, large }: {
  label: string; value: number; accent?: string; pct?: number; large?: boolean
}) {
  const { t, i18n } = useTranslation()
  const isEs = i18n.language === 'es'
  // PR-F3: safe money — invalid value shows "Unavailable" (muted), never $NaN.
  const money = formatMoney(value, { isEs })
  const pctDisplay = pct !== undefined ? formatPercent(pct, isEs).displayValue : null
  return (
    // PR-F3A.1: min-w-0 lets the card shrink inside the grid at 390px; break-words +
    // tabular-nums keep long money values from forcing horizontal page overflow.
    <div className={`min-w-0 bg-[#FAFAF8] border border-[#ECEAE4] rounded-lg p-3 ${large ? 'lg:col-span-1' : ''}`}>
      <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">{label}</div>
      <div data-testid="aia-summary-value" className={`${large ? 'text-[22px]' : 'text-[16px]'} font-[900] break-words tabular-nums`} style={{ color: money.isValid ? (accent || '#1C1A17') : '#9E9890' }}>
        {money.displayValue}
      </div>
      {pctDisplay !== null && (
        <div className="text-[9px] font-[700] text-[#9E9890] mt-0.5">{pctDisplay} {(pct ?? 0) > 0 ? t('aiaBilling.complete') : ''}</div>
      )}
    </div>
  )
}
